import { NODE_RADIUS } from './constants'
import type { EventKind, GraphEdge, GraphNode } from './types'

const node = (
  id: string,
  label: string,
  kind: GraphNode['kind'],
  facts: GraphNode['facts']
): GraphNode => ({
  id,
  label,
  kind,
  radius: NODE_RADIUS[kind],
  facts,
  sample: true
})

/** Mock clusters — illustrative until their backends exist. */
export const MOCK_NODES: GraphNode[] = [
  // Databases
  node('db-pg', 'PostgreSQL', 'database', [
    { label: 'Engine', value: 'Postgres 16' },
    { label: 'Region', value: 'eu-west-1' },
    { label: 'Size', value: '42 GB' },
    { label: 'Connections', value: '18 / 100' }
  ]),
  node('db-redis', 'Redis', 'database', [
    { label: 'Engine', value: 'Redis 7' },
    { label: 'Hit rate', value: '98.2%' },
    { label: 'Keys', value: '1.2M' }
  ]),
  node('db-click', 'ClickHouse', 'database', [
    { label: 'Engine', value: 'ClickHouse 24' },
    { label: 'Region', value: 'eu-west-1' },
    { label: 'Rows', value: '4.8B' },
    { label: 'Ingest', value: '12k / s' }
  ]),
  node('db-mongo', 'MongoDB', 'database', [
    { label: 'Engine', value: 'Mongo 7' },
    { label: 'Collections', value: '142' },
    { label: 'Documents', value: '86M' }
  ]),
  node('db-s3', 'Object Storage', 'database', [
    { label: 'Provider', value: 'S3' },
    { label: 'Buckets', value: '9' },
    { label: 'Objects', value: '2.1M' },
    { label: 'Size', value: '1.4 TB' }
  ]),

  // Email
  node('email-smtp', 'SMTP Relay', 'email', [
    { label: 'Provider', value: 'Postmark' },
    { label: 'Sent (24h)', value: '3,481' },
    { label: 'Bounce', value: '0.4%' }
  ]),
  node('email-gmail', 'Gmail Sync', 'email', [
    { label: 'Accounts', value: '12' },
    { label: 'Last sync', value: '2m ago' }
  ]),
  node('email-sendgrid', 'SendGrid', 'email', [
    { label: 'Provider', value: 'SendGrid' },
    { label: 'Sent (24h)', value: '12,940' },
    { label: 'Open rate', value: '41%' }
  ]),

  // AI Agents
  node('agent-support', 'Support Bot', 'agent', [
    { label: 'Grant', value: 'client credentials' },
    { label: 'Scopes', value: '3 apps' },
    { label: 'Last run', value: '5m ago' }
  ]),
  node('agent-sync', 'Data Sync', 'agent', [
    { label: 'Grant', value: 'client credentials' },
    { label: 'Scopes', value: '1 app' },
    { label: 'Last run', value: '1h ago' }
  ]),
  node('agent-billing', 'Billing Agent', 'agent', [
    { label: 'Grant', value: 'client credentials' },
    { label: 'Scopes', value: '2 apps' },
    { label: 'Last run', value: '12m ago' }
  ]),
  node('agent-onboard', 'Onboarding Agent', 'agent', [
    { label: 'Grant', value: 'authorization code' },
    { label: 'Scopes', value: '1 app' },
    { label: 'Last run', value: '3m ago' }
  ]),
  node('agent-security', 'Security Scanner', 'agent', [
    { label: 'Grant', value: 'client credentials' },
    { label: 'Scopes', value: '4 apps' },
    { label: 'Last run', value: '27m ago' }
  ]),
  node('agent-report', 'Report Generator', 'agent', [
    { label: 'Grant', value: 'client credentials' },
    { label: 'Scopes', value: '2 apps' },
    { label: 'Last run', value: '1h ago' }
  ])
]

const edge = (source: string, target: string, flow?: boolean): GraphEdge => ({
  id: `${source}->${target}`,
  source,
  target,
  flow
})

/**
 * Edges among mock nodes + from mock nodes to the company core (id `company`).
 * The cross-node mesh (agent→db, agent→email, db→db) is what makes the live
 * engine emit interaction events — `useLiveEvents.interactionFor` derives the
 * event kind/text from each edge's endpoint kinds, so no extra event templates
 * are needed for these to animate.
 */
export const MOCK_EDGES: GraphEdge[] = [
  // Company core → each cluster
  edge('company', 'db-pg', true),
  edge('company', 'db-redis'),
  edge('company', 'db-click', true),
  edge('company', 'db-mongo'),
  edge('company', 'db-s3'),
  edge('company', 'email-smtp', true),
  edge('company', 'email-gmail'),
  edge('company', 'email-sendgrid', true),
  edge('company', 'agent-support', true),
  edge('company', 'agent-sync', true),
  edge('company', 'agent-billing', true),
  edge('company', 'agent-onboard'),
  edge('company', 'agent-security', true),
  edge('company', 'agent-report'),

  // Agents acting on data / email systems
  edge('agent-billing', 'db-pg', true),
  edge('agent-billing', 'email-sendgrid', true),
  edge('agent-report', 'db-click', true),
  edge('agent-report', 'db-pg'),
  edge('agent-security', 'db-pg'),
  edge('agent-onboard', 'db-mongo', true),
  edge('agent-onboard', 'email-smtp'),
  edge('agent-support', 'db-redis'),

  // Data plane: ETL into the warehouse + nightly backup to object storage
  edge('db-click', 'db-pg'),
  edge('db-pg', 'db-s3', true)
]

/** Template for a synthesized live event (text resolved by caller via i18n). */
export type EventTemplate = {
  nodeId: string
  edgeId?: string
  kind: EventKind
  /** i18n key under workspaceOverview.companyGraph.events.* */
  textKey: string
  /** interpolation params for the i18n text (e.g. { target: 'Web App' }) */
  params?: Record<string, string>
}

/** Pool the simulated engine samples from (mock-node events). */
export const MOCK_EVENT_TEMPLATES: EventTemplate[] = [
  {
    nodeId: 'db-pg',
    edgeId: 'company->db-pg',
    kind: 'ok',
    textKey: 'backupDone'
  },
  { nodeId: 'db-pg', kind: 'warn', textKey: 'slowQuery' },
  { nodeId: 'db-redis', kind: 'ok', textKey: 'failoverPassed' },
  { nodeId: 'db-click', kind: 'warn', textKey: 'slowQuery' },
  {
    nodeId: 'db-mongo',
    edgeId: 'company->db-mongo',
    kind: 'ok',
    textKey: 'backupDone'
  },
  { nodeId: 'db-s3', kind: 'ok', textKey: 'backupDone' },
  {
    nodeId: 'email-smtp',
    edgeId: 'company->email-smtp',
    kind: 'info',
    textKey: 'emailsSent'
  },
  { nodeId: 'email-smtp', kind: 'warn', textKey: 'emailBounced' },
  { nodeId: 'email-gmail', kind: 'ok', textKey: 'mailboxSynced' },
  {
    nodeId: 'email-sendgrid',
    edgeId: 'company->email-sendgrid',
    kind: 'info',
    textKey: 'emailsSent'
  },
  { nodeId: 'email-sendgrid', kind: 'warn', textKey: 'emailBounced' },
  {
    nodeId: 'agent-support',
    edgeId: 'company->agent-support',
    kind: 'action',
    textKey: 'ticketsResolved'
  },
  { nodeId: 'agent-sync', kind: 'warn', textKey: 'agentRetry' },
  {
    nodeId: 'agent-sync',
    edgeId: 'company->agent-sync',
    kind: 'ok',
    textKey: 'recordsSynced'
  },
  {
    nodeId: 'agent-billing',
    edgeId: 'company->agent-billing',
    kind: 'ok',
    textKey: 'recordsSynced'
  },
  { nodeId: 'agent-onboard', kind: 'ok', textKey: 'recordsSynced' },
  { nodeId: 'agent-security', kind: 'warn', textKey: 'agentRetry' },
  { nodeId: 'agent-report', kind: 'ok', textKey: 'recordsSynced' }
]
